import { useQuery } from "@tanstack/react-query";
import { subscriptionApi } from "../api/subscription";
import { useAuthStore } from "../store/authStore";

/**
 * Reads a plan feature flag off the subscription's `features` JSONB.
 *
 * An absent key is `false`, so a new flag is off for everyone until a
 * super-admin switches it on for a plan or via the org's plan overrides — no
 * migration and no backfill.
 *
 * There is deliberately no local-mode bypass here: local mode has no
 * subscription to read, so a bypass would be a second path to a screen the flag
 * exists to hide. `isLoading` is only ever true while a real request is in
 * flight, so callers that wait on it do not hang in local mode.
 */
export function usePlanFeature(name: string): { enabled: boolean; isLoading: boolean } {
  const user = useAuthStore((s) => s.user);

  const { data, isLoading } = useQuery({
    queryKey: ["subscription-status"],
    queryFn: () => subscriptionApi.getStatus(),
    staleTime: 60000,
    enabled: !!user,
  });

  return { enabled: data?.features?.[name] === true, isLoading };
}
